import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "./api";
import { inferUnits } from "./units";

const AuthContext = createContext(null);

const PROFILE_KEYS = ["name", "displayName", "city", "country", "preferredUnits", "gender", "avatar", "visitedCountries", "visitedWonders", "visitedNaturalWonders", "pushEnabled", "heightCm", "calorieGoal", "waterGoalMl", "proteinGoal", "plan", "weeklyGoal", "preferredCoach", "streakReminderEnabled", "streakReminderTime", "streakReminderTz", "weeklyDigestEnabled", "weeklyDigestDay", "weeklyDigestTime", "weeklyDigestTz"];

export function AuthProvider({ children }) {
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (api.getToken()) {
        try {
          const m = await api.me();
          // Auto-sync units to the member's locale (US → imperial) if never set.
          if (m && !m.preferredUnits) {
            const u = inferUnits();
            m.preferredUnits = u;
            api.updateMe({ preferredUnits: u }).catch(() => {});
          }
          setMember(m);
        } catch {
          api.clearToken();
        }
      }
      setLoading(false);
    })();
  }, []);

  const login = useCallback(async (email, password) => {
    const { token, member } = await api.login({ email, password });
    api.setToken(token);
    setMember(member);
    return member;
  }, []);

  const signup = useCallback(async (data) => {
    const { token, member } = await api.signup(data);
    api.setToken(token);
    setMember(member);
    return member;
  }, []);

  // Optimistically merges local fields (points/streak from endpoints) and
  // persists any profile fields to the backend.
  const updateMember = useCallback((patch) => {
    setMember((prev) => ({ ...prev, ...patch }));
    const toPersist = Object.fromEntries(
      Object.entries(patch).filter(([k]) => PROFILE_KEYS.includes(k))
    );
    if (Object.keys(toPersist).length) {
      api.updateMe(toPersist).then((m) => setMember(m)).catch(() => {});
    }
  }, []);

  const logout = useCallback(() => {
    api.clearToken();
    setMember(null);
  }, []);

  const refreshMember = useCallback(async () => {
    try { const m = await api.me(); setMember(m); return m; } catch { return null; }
  }, []);

  return (
    <AuthContext.Provider value={{ member, loading, login, signup, logout, updateMember, refreshMember }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
